import React, { useState } from 'react';
import { Modal, Box, TextField, Button, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import './TaskModal.css';

interface TaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (title: string, description: string, estimatedLength: number, dueDate: Date, priority: 'low' | 'medium' | 'high', status: 'pending' | 'inProgress' | 'done') => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, onSave }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [estimatedLength, setEstimatedLength] = useState<number>(0);
    const [dueDate, setDueDate] = useState<Date | null>(null);
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('low');
    const [status, setStatus] = useState<'pending' | 'inProgress' | 'done'>('pending');

    const handleSave = () => {
        if (dueDate) {
            onSave(title, description, estimatedLength, dueDate, priority, status);
            onClose();
            setTitle('');
            setDescription('');
            setEstimatedLength(0);
            setDueDate(null);
            setPriority('low');
            setStatus('pending');
        }
    };

    const isSaveDisabled = !title || !estimatedLength || !dueDate;

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box className="modal-box">
                <h2>Create New Task</h2>
                <TextField
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={4}
                />
                <div className="inline-fields">
                    <TextField
                        label="Estimated Length (hours)"
                        type="number"
                        value={estimatedLength}
                        onChange={(e) => setEstimatedLength(Number(e.target.value))}
                    />
                    <DatePicker
                        label="Due Date"
                        value={dueDate}
                        onChange={(date) => setDueDate(date)}
                    />
                </div>
                <FormControl fullWidth margin="normal">
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select
                        labelId="priority-label"
                        label="Priority"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                    >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                        labelId="status-label"
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as 'pending' | 'inProgress' | 'done')}
                    >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="inProgress">In Progress</MenuItem>
                        <MenuItem value="done">Done</MenuItem>
                    </Select>
                </FormControl>
                <div className="modal-actions">
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaveDisabled}>
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};

export default TaskModal;
